<script setup lang="ts">
import type { ContentPickerSourceMenuListItem } from '@@/bits/content_picker'
import { ContentPickerSource } from '@@/bits/content_picker'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import type { SearchFilters } from '@@/bits/web_search'
import ChicletButton from '@@/vuecomponents/ChicletButton.vue'
import { computed, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    menuList: Array<Array<ContentPickerSourceMenuListItem>>
    clipboardText: string
    xClipboardButton: boolean
    isPhoneScreen: boolean
    isDisabledOnDrag?: boolean
  }>(),
  {
    isDisabledOnDrag: false,
  },
)

const emit = defineEmits<{
  (name: 'close'): void
  (name: 'paste-text'): void
  (name: 'focus-search-bar', payload: SearchFilters): void
  (name: 'select', payload: ContentPickerSource): void
  (name: 'select-link', url: string): void
  (name: 'trigger-link-input'): void
  (name: 'trigger-ai-image-search'): void
}>()

const displayedSourceMenuLists = computed<Array<Array<ContentPickerSourceMenuListItem>>>(() => {
  const visibleItemLists = props.menuList
    .map((list) => list.filter((item) => item.visible))
    .filter((list) => list.length > 0)
  const displayedLists: Array<Array<ContentPickerSourceMenuListItem>> = []

  // If 2 adjacent lists have less than or equal to 3 items, we group them together
  let i = 0
  let bucket: ContentPickerSourceMenuListItem[] = []
  while (i < visibleItemLists.length) {
    bucket.push(...visibleItemLists[i])
    if (bucket.length >= 3) {
      displayedLists.push(bucket)
      bucket = []
    }
    i++
  }
  if (bucket.length > 0) displayedLists.push(bucket)

  // If total items count is less than or equal to 9, we group all lists together
  const itemsCount = displayedLists.reduce((count, list) => count + list.length, 0)
  return itemsCount <= 9 ? [displayedLists.flat()] : displayedLists
})

watch(displayedSourceMenuLists, (lists) => {
  // When there's nothing else to show in the menu, close it
  if (lists.flat(Infinity).length === 0) {
    emit('close')
  }
})

// Triggers <input file> element and use camera directly
const triggerNativeInputWithCamera = (id: string): void => {
  const input = document.getElementById(id)
  if (input) {
    input.setAttribute('capture', 'environment')
    input.click()
    input.removeAttribute('capture')
  }
}

const onClick = (item: ContentPickerSourceMenuListItem) => {
  if (item.hiddenInputId) {
    if (
      device.mobile &&
      item.type !== ContentPickerSource.FileBrowser // For file browser, don't open camera
    ) {
      triggerNativeInputWithCamera(item.hiddenInputId)
    } else {
      document.getElementById(item.hiddenInputId)?.click()
    }
  }

  emit(item.actionType as any, item.actionPayload as any)
}
</script>

<template>
  <div class="pb-4 px-4 mt-0 @tablet-portrait/surface-container:mt-3 tablet-portrait:-@:mt-3">
    <div
      v-if="isPhoneScreen && xClipboardButton"
      :class="[
        'grid grid-flow-row',
        'grid-cols-1 @tablet-portrait/surface-container:grid-cols-3 tablet-portrait:-@:grid-cols-3',
        'gap-0 @tablet-portrait/surface-container:gap-3 tablet-portrait:-@:gap-3',
        'mb-4',
      ]"
    >
      <ChicletButton
        :class="['rounded-2.5xl @tablet-portrait/surface-container:rounded-xl tablet-portrait:-@:rounded-xl']"
        :disabled="isDisabledOnDrag"
        color="pink"
        icon-name="clipboard_outline"
        :aria-label="__('Paste from clipboard. Your clipboard text is: %{clipboardText}', { clipboardText })"
        :text="__('Clipboard')"
        :subtext="clipboardText"
        @click="$emit('paste-text')"
      />
    </div>

    <div
      v-for="(list, index) in displayedSourceMenuLists"
      :key="index"
      :class="[
        'grid grid-flow-row',
        'grid-cols-1 @tablet-portrait/surface-container:grid-cols-3 tablet-portrait:-@:grid-cols-3',
        'gap-0 @tablet-portrait/surface-container:gap-3 tablet-portrait:-@:gap-3',
        {
          'mt-4 @tablet-portrait/surface-container:mt-6 tablet-portrait:-@:mt-6': index > 0,
        },
      ]"
    >
      <template v-for="(item, itemIndex) in list">
        <ChicletButton
          :key="item.text"
          :class="[
            'first:rounded-t-2.5xl @tablet-portrait/surface-container:first:rounded-t-xl tablet-portrait:-@:first:rounded-t-xl',
            'last:rounded-b-2.5xl @tablet-portrait/surface-container:last:rounded-b-xl tablet-portrait:-@:last:rounded-b-xl',
            '@tablet-portrait/surface-container:rounded-xl tablet-portrait:-@:rounded-xl',
            isPhoneScreen && itemIndex < list.length - 1 && 'horizontal-bottom-separator',
          ]"
          :aria-label="item.ariaLabel"
          :disabled="item.disabled && isDisabledOnDrag"
          :hidden-input-id="item.hiddenInputId"
          :color="item.color"
          :icon-name="item.icon"
          :avatar-url="item.avatarUrl"
          :avatar-alt-text="item.avatarAltText"
          :x-new-badge="item.isNew"
          :data-testid="item.testId"
          :text="item.text"
          @click="() => onClick(item)"
        />
      </template>
    </div>
  </div>
</template>

<style lang="postcss">
.horizontal-bottom-separator::after {
  @apply bg-highway-disabled dark:bg-white-disabled;
  @apply absolute bottom-0 start-12 end-4;
  @apply h-[0.5px];
  @apply content-[""];
}
</style>
